import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../../../../utils/api';
import { sortFeatureValues } from '../../../../utils/sortingUtils';
import { defaultCardLayout } from '../../../../utils/valuePresentation';
import { FeatureProperties } from '../../../FamiliesPage/components/FamilyEditor/FeatureProperties';
import ModalPortal from '../../../../components/ModalPortal';
import { ValueProperties } from '../../../FamiliesPage/components/FamilyEditor/ValueProperties.jsx';
import {
    handleSelectFeature,
    handleAddAttrRow,
    handleRemoveAttrRow,
    handleAttrChange,
    handleValueFieldChange,
    handleValuePropertyChange,
    handleAddValueAttr,
    handleRemoveValueAttr,
    handleValueAttrChange
} from '../../../FamiliesPage/utils/handlers';
import useUsageDeleteConfirm from '../../../../hooks/useUsageDeleteConfirm';

const SUGGESTIONS = ['friendlyName', 'mappnc'];
const noop = () => {};

/**
 * ManageFeatureModal - Family feature editor in a modal
 */
export default function ManageFeatureModal({ feature, familyId, onClose, onRuleCellsChanged }) {
    const [activeFeature, setActiveFeature] = useState(feature);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedFeatureId, setSelectedFeatureId] = useState(feature.id);
    const [friendlyName, setFriendlyName] = useState('');
    const [description, setDescription] = useState('');
    const [inputType, setInputType] = useState('dropdown');
    const [isSystem, setIsSystem] = useState(false);
    const [isOutput, setIsOutput] = useState(false);
    const [valueSchema, setValueSchema] = useState([]);
    const [cardLayout, setCardLayout] = useState(null);
    const [customAttrs, setCustomAttrs] = useState([]);
    const [featureValues, setFeatureValues] = useState([]);
    const [selectedValueId, setSelectedValueId] = useState(null);
    const [savingFeature, setSavingFeature] = useState(false);
    const [isEditingFeatureName, setIsEditingFeatureName] = useState(false);
    const [editFeatureNameText, setEditFeatureNameText] = useState('');
    const [isSavingFeatureName, setIsSavingFeatureName] = useState(false);
    const [isEditingValueName, setIsEditingValueName] = useState(false);
    const [editValueNameText, setEditValueNameText] = useState('');
    const [isSavingValueName, setIsSavingValueName] = useState(false);
    const [newValText, setNewValText] = useState('');
    const { confirmDelete, modal: deleteUsageModal } = useUsageDeleteConfirm();
    const featureRef = useRef(feature);
    featureRef.current = feature;
    const featureId = feature.id;

    const applyFeature = useMemo(
        () => handleSelectFeature(
            setSelectedFeatureId,
            setFriendlyName,
            setDescription,
            setInputType,
            setIsEditingFeatureName,
            setIsEditingValueName,
            setCustomAttrs,
            setFeatureValues,
            setSelectedValueId,
            noop,
            noop,
            noop,
            setValueSchema,
            setIsSystem,
            setCardLayout,
            setIsOutput
        ),
        []
    );

    const loadFeature = useCallback(async (keepSelectedValueId = null) => {
        const apply = (feat, valueId) => {
            featureRef.current = feat;
            setActiveFeature(feat);
            applyFeature(feat, valueId);
        };

        if (familyId && familyId !== 'global') {
            try {
                const res = await apiFetch(`/api/products/${familyId}/features?t=${Date.now()}`);
                if (res.ok) {
                    const features = await res.json();
                    const latest = features.find(f => f.id === featureId);
                    if (latest) {
                        apply(latest, keepSelectedValueId);
                        return latest;
                    }
                }
            } catch (err) {
                console.error(err);
            }
        }

        apply(featureRef.current, keepSelectedValueId);
        return featureRef.current;
    }, [applyFeature, familyId, featureId]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setIsLoading(true);
            await loadFeature();
            if (!cancelled) setIsLoading(false);
        })();
        return () => { cancelled = true; };
    }, [loadFeature]);

    const selectedValue = featureValues.find(val => val.id === selectedValueId) || null;
    const selectedValueIndex = featureValues.findIndex(val => val.id === selectedValueId);

    const notifyChanged = () => {
        onRuleCellsChanged?.();
    };

    const handleSaveFeatureProperties = async (e) => {
        e?.preventDefault();
        if (!selectedFeatureId) return;

        setSavingFeature(true);
        try {
            const attrsObj = {};
            customAttrs.forEach(item => {
                if (item.key.trim()) {
                    attrsObj[item.key.trim()] = item.value.trim();
                }
            });

            const valuesPayload = featureValues.map(val => {
                const valAttrs = {};
                (val.attributes || []).forEach(attr => {
                    if (attr.key.trim()) {
                        valAttrs[attr.key.trim()] = attr.value.trim();
                    }
                });
                return {
                    id: val.id,
                    value: val.value,
                    displayText: val.displayText,
                    description: val.description,
                    imageUrl: val.imageUrl,
                    isDefault: Boolean(val.isDefault),
                    sortOrder: Number(val.sortOrder || 0),
                    propertyValues: val.propertyValues || {},
                    attributes: valAttrs
                };
            });

            const res = await apiFetch(`/api/features/${selectedFeatureId}/properties`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    displayText: friendlyName,
                    description,
                    inputType,
                    attributes: attrsObj,
                    valueSchema,
                    cardLayout: cardLayout || (String(inputType).toLowerCase() === 'valuecards' ? defaultCardLayout(valueSchema) : null),
                    isSystem,
                    isOutput,
                    values: valuesPayload
                })
            });

            if (res.ok) {
                onClose();
            } else {
                alert(`Error saving properties: ${await res.text()}`);
            }
        } catch (err) {
            console.error('Failed to save feature properties', err);
        } finally {
            setSavingFeature(false);
        }
    };

    const handleRenameFeatureSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!selectedFeatureId || !editFeatureNameText.trim()) return;
        setIsSavingFeatureName(true);
        try {
            const res = await apiFetch(`/api/features/${selectedFeatureId}/rename`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Name: editFeatureNameText.trim() })
            });
            if (res.ok) {
                await loadFeature(selectedValueId);
                setIsEditingFeatureName(false);
                notifyChanged();
            } else {
                alert(`Error renaming feature: ${await res.text()}`);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsSavingFeatureName(false);
        }
    };

    const handleRenameValueSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!selectedFeatureId || !selectedValueId || !editValueNameText.trim()) return;
        setIsSavingValueName(true);
        try {
            const res = await apiFetch(`/api/features/${selectedFeatureId}/values/${selectedValueId}/rename`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Value: editValueNameText.trim() })
            });
            if (res.ok) {
                await loadFeature(selectedValueId);
                setIsEditingValueName(false);
                notifyChanged();
            } else {
                alert(`Error renaming value: ${await res.text()}`);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsSavingValueName(false);
        }
    };

    const handleDeleteFeature = async () => {
        if (!await confirmDelete({ kind: 'feature', featureId: selectedFeatureId, name: activeFeature?.name })) return;
        try {
            const res = await apiFetch(`/api/features/${selectedFeatureId}`, { method: 'DELETE' });
            if (res.ok) {
                notifyChanged();
                onClose();
            } else {
                alert(`Failed to delete feature: ${await res.text()}`);
            }
        } catch (err) {
            console.error(err);
            alert('Failed to connect to the Rule Engine server.');
        }
    };

    const handleDeleteValue = async () => {
        if (!selectedFeatureId || !selectedValueId) return;
        const valText = selectedValue?.value || '';
        if (!await confirmDelete({ kind: 'value', featureId: selectedFeatureId, valueId: selectedValueId, name: valText })) return;
        try {
            const res = await apiFetch(`/api/features/${selectedFeatureId}/values/${selectedValueId}`, { method: 'DELETE' });
            if (res.ok) {
                const deletedId = selectedValueId;
                setFeatureValues(prev => prev.filter(v => v.id !== deletedId));
                setSelectedValueId(null);
                featureRef.current = {
                    ...featureRef.current,
                    values: (featureRef.current.values || []).filter(v => v.id !== deletedId)
                };
                const latest = await loadFeature();
                if (latest) {
                    featureRef.current = latest;
                }
                notifyChanged();
            } else {
                alert(`Failed to delete value: ${await res.text()}`);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleAddValue = async (e) => {
        e.preventDefault();
        if (!newValText.trim() || !selectedFeatureId) return;
        try {
            const res = await apiFetch(`/api/features/${selectedFeatureId}/values`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Value: newValText.trim() })
            });
            if (res.ok) {
                const data = await res.json();
                const newValId = data.id || data.Id || data.ID;
                setNewValText('');
                await loadFeature(newValId);
            } else {
                alert(`Failed to add value: ${await res.text()}`);
            }
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <ModalPortal>
        <div className="modal-overlay fade-in">
            <div
                className="modal-content"
                style={{
                    width: '92vw',
                    maxWidth: '92vw',
                    height: '88vh',
                    maxHeight: '88vh',
                    padding: '1.25rem 1.5rem',
                    borderRadius: 'var(--radius)',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border)',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                    overflow: 'hidden'
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                    <div>
                        <h2 style={{ fontSize: '1.15rem', fontWeight: 700, margin: 0 }}>Feature management</h2>
                        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0.15rem 0 0 0' }}>Manage feature details and values</p>
                    </div>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: 'var(--text-secondary)' }} title="Close">
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                </div>

                {isLoading ? (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>
                        Loading feature...
                    </div>
                ) : (
                    <div style={{ flex: 1, display: 'flex', gap: '1.25rem', minHeight: 0, border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg-card)', padding: '1.25rem' }}>
                        <div style={{ width: '210px', minWidth: '210px', display: 'flex', flexDirection: 'column', gap: '0.5rem', borderRight: '1px solid var(--border)', paddingRight: '1rem', overflow: 'hidden' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.25rem 0.35rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                                <i className="fa-solid fa-cube" style={{ fontSize: '0.75rem', color: 'var(--accent)' }}></i>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activeFeature?.name}</span>
                            </div>
                            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.15rem', borderLeft: '1px dotted var(--border)', marginLeft: '0.85rem', paddingLeft: '0.5rem' }}>
                                {sortFeatureValues(featureValues).map(val => {
                                    const isValueSelected = selectedValueId === val.id;
                                    return (
                                        <div
                                            key={val.id}
                                            onClick={() => setSelectedValueId(val.id)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.35rem',
                                                padding: '0.15rem 0.25rem',
                                                fontSize: '0.75rem',
                                                borderRadius: '4px',
                                                backgroundColor: isValueSelected ? 'var(--bg-secondary)' : 'transparent',
                                                color: isValueSelected ? 'var(--text-primary)' : 'var(--text-secondary)',
                                                fontWeight: isValueSelected ? 600 : 400,
                                                cursor: 'pointer'
                                            }}
                                            className="table-row-hover"
                                        >
                                            <i className="fa-solid fa-circle" style={{ fontSize: '0.25rem', color: isValueSelected ? 'var(--accent)' : 'var(--text-secondary)', opacity: isValueSelected ? 0.8 : 0.5 }}></i>
                                            <span style={{ fontFamily: 'monospace', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val.value}</span>
                                            {val.isDefault && (
                                                <span style={{ fontSize: '0.6rem', fontWeight: 600, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.03em', flexShrink: 0 }}>Default</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            <form onSubmit={handleAddValue} style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                                <input
                                    className="input-field"
                                    placeholder="Add value..."
                                    value={newValText}
                                    onChange={e => setNewValText(e.target.value)}
                                    style={{ fontSize: '0.75rem', padding: '2px 4px', width: '110px', margin: 0, height: '24px' }}
                                />
                                <button
                                    type="submit"
                                    className="btn-secondary"
                                    disabled={!newValText.trim()}
                                    style={{ padding: '2px 6px', fontSize: '0.7rem', height: '24px' }}
                                >
                                    Add
                                </button>
                            </form>
                        </div>

                        <div style={{ flex: 1, display: 'flex', gap: '1.5rem', overflow: 'hidden', minWidth: 0 }}>
                            <FeatureProperties
                                activeFeature={activeFeature}
                                isEditingFeatureName={isEditingFeatureName}
                                editFeatureNameText={editFeatureNameText}
                                setEditFeatureNameText={setEditFeatureNameText}
                                setIsEditingFeatureName={setIsEditingFeatureName}
                                isSavingFeatureName={isSavingFeatureName}
                                isSystem={isSystem}
                                setIsSystem={setIsSystem}
                                isOutput={isOutput}
                                setIsOutput={setIsOutput}
                                customAttrs={customAttrs}
                                familyId={familyId !== 'global' ? familyId : null}
                                features={[]}
                                savingFeature={savingFeature}
                                suggestions={SUGGESTIONS}
                                onRenameFeatureSubmit={handleRenameFeatureSubmit}
                                onAddAttrRow={handleAddAttrRow(setCustomAttrs)}
                                onRemoveAttrRow={handleRemoveAttrRow(setCustomAttrs)}
                                onAttrChange={handleAttrChange(setCustomAttrs)}
                                onDeleteFeature={handleDeleteFeature}
                                onClearSelection={onClose}
                                showActionBar={false}
                            />
                            <ValueProperties
                                selectedValue={selectedValue}
                                selectedValueIndex={selectedValueIndex}
                                isEditingValueName={isEditingValueName}
                                editValueNameText={editValueNameText}
                                setEditValueNameText={setEditValueNameText}
                                setIsEditingValueName={setIsEditingValueName}
                                isSavingValueName={isSavingValueName}
                                featureValues={featureValues}
                                onRenameValueSubmit={handleRenameValueSubmit}
                                onValueFieldChange={handleValueFieldChange(setFeatureValues)}
                                onValuePropertyChange={handleValuePropertyChange(setFeatureValues)}
                                valueSchema={valueSchema}
                                onAddValueAttr={handleAddValueAttr(setFeatureValues)}
                                onRemoveValueAttr={handleRemoveValueAttr(setFeatureValues)}
                                onValueAttrChange={handleValueAttrChange(setFeatureValues)}
                                onDeleteValue={handleDeleteValue}
                                emptyMessage="Select a value on the left to manage its details."
                            />
                        </div>
                    </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', flexShrink: 0, borderTop: '1px solid var(--border)', paddingTop: '0.85rem' }}>
                    <button
                        type="button"
                        className="btn-secondary"
                        onClick={onClose}
                        style={{ fontSize: '0.8rem', padding: '0.4rem 1rem' }}
                    >
                        Close
                    </button>
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={handleSaveFeatureProperties}
                        disabled={isLoading || savingFeature}
                        style={{ background: 'var(--text-primary)', color: 'var(--bg-primary)', fontSize: '0.8rem', padding: '0.4rem 1rem' }}
                    >
                        {savingFeature ? 'Saving...' : 'Save and close'}
                    </button>
                </div>
            </div>
            {deleteUsageModal}
        </div>
        </ModalPortal>
    );
}
